import { useNavigate, useParams } from "react-router-dom";
import styles from "./TenantsAdministrationTenant.module.scss";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { Icon, IconType } from "../../../../common/icons/Icon";
import {
  constructEditTenantHref,
  constructStudioHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import {
  ITenantAdministrationTenantStudioModel,
  ITenantsAdministrationTenantModel,
} from "./models/tenantsAdministrationTenantModel";
import { useMemo } from "react";
import { DefaultSection } from "../../../../common/components/common/DefaultSection";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { classNames } from "primereact/utils";
import { SpecificPageNotFound } from "../../../../common/components/errorPages/SpecificPageNotFound";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { NotesButton } from "../../../../common/components/common/NotesDialog/NotesButton";
import { SectionInfoRow } from "../../../../common/components/common/SectionInfoRow";

export const TenantsAdministrationTenant = () => {
  const { id } = useParams();

  const { data: result, isLoading } =
    useApiQuery<ITenantsAdministrationTenantModel>({
      queryKey: [endpointsMetadata.tenant.getTenant, id],
      endpoint: `${endpointsMetadata.tenant.getTenant}?tenantId=${id}`,
      enabled: id !== undefined,
      globalIsLoading: false,
    });

  const currentTenant = useMemo(() => result, [result]);

  useAddAdditionalLayoutButton({
    iconType: IconType.EditIcon,
    href: constructEditTenantHref(Number(id)),
    rawPath: routePaths.tenantEdit,
  });

  const navigate = useNavigate();

  const backToTenants = () => {
    navigate(routePaths.tenants);
  };

  if (!isLoading && !currentTenant) {
    return (
      <SpecificPageNotFound
        text={"За съжаление няма такъв бизнес клиент."}
        buttonText={"Обратно към списъка с бизнес клиенти"}
        onButtonClick={backToTenants}
      />
    );
  }

  return (
    <div className={styles.root}>
      <DefaultSection className={styles.root_title}>
        <div className={styles.root_header_title}>
          <SkeletonItem isLoading={isLoading} value={currentTenant?.names} />
        </div>
        <div className={styles.root_header_email}>
          <SkeletonItem
            isLoading={isLoading}
            value={currentTenant?.email}
            width={200}
          />
        </div>
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        <SectionInfoRow
          label={"ID"}
          data={currentTenant?.id}
          isLoading={isLoading}
          skekletonWidth={50}
        />

        <SectionInfoRow
          label={"Телефонен номер"}
          data={currentTenant?.phoneNumber}
          isLoading={isLoading}
          skekletonWidth={150}
        />

        <SectionInfoRow
          label={"Добавен на"}
          data={dateUtility.formatDate(currentTenant?.createdOn)}
          isLoading={isLoading}
          skekletonWidth={120}
        />

        <SectionInfoRow
          label={"Прекратен на"}
          data={dateUtility.formatDate(currentTenant?.cancelledOn)}
          isLoading={isLoading}
          skekletonWidth={110}
        />
      </DefaultSection>

      <ItemsList
        isLoading={isLoading}
        className={styles.list}
        numberOfItems={5}
        title="Активни обекти"
        noItemsText="Не са открити активни обекти."
        actionButton={
          <NotesButton
            entityId={result?.id}
            label={"Бележки"}
            getEndpoint={endpointsMetadata.tenant.getNotes}
            createEndpoint={endpointsMetadata.tenant.addNote}
            updateEndpoint={endpointsMetadata.tenant.updateNote}
            deleteEndpoint={endpointsMetadata.tenant.deleteNote}
            noResultsLabel={"Към момента няма добавени бележки за този клиент."}
          />
        }
        itemTemplate={(item, index, actionButtons, className) => {
          return (
            <TenantsAdministrationTenantUserItem
              className={className}
              item={item}
              key={item?.id ?? index}
              isLoading={isLoading}
              actionButtons={actionButtons}
            />
          );
        }}
        items={currentTenant?.studios ?? []}
        swipeLeftActions={[
          {
            method: "delete",
            enablePrompt: true,
            promptMessage: "Сигурни ли сте, че искате да изтриете този обект?",
            endpoint: endpointsMetadata.tenant.deleteStudio,
            iconType: IconType.DeleteIcon,
            additionalParams: () => `tenantId=${currentTenant?.id}`,
            validator: (item: ITenantAdministrationTenantStudioModel) =>
              !item.isDeleted,
            invalidateKeys: [
              [endpointsMetadata.tenant.getTenant],
              [endpointsMetadata.initialization.getUserStudios],
            ],
          },
          {
            method: "put",
            enablePrompt: true,
            promptMessage: "Сигурни ли сте, че искате да възстановите обекта?",
            endpoint: endpointsMetadata.tenant.putStudioBack,
            iconType: IconType.PutBackIcon,
            additionalParams: () => `tenantId=${currentTenant?.id}`,
            validator: (item: ITenantAdministrationTenantStudioModel) =>
              item.isDeleted,
            invalidateKeys: [
              [endpointsMetadata.tenant.getTenant],
              [endpointsMetadata.initialization.getUserStudios],
            ],
          },
        ]}
        swipeRightActions={[
          {
            type: "edit",
            iconType: IconType.EditIcon,
            endpoint: routePaths.studioEdit,
          },
        ]}
        enableSearch
        fullHeight
      />
    </div>
  );
};

interface TenantsAdministrationTenantUserItemProps {
  className?: string;
  item: ITenantAdministrationTenantStudioModel | undefined;
  isLoading: boolean;
  actionButtons?: React.ReactNode;
}

export const TenantsAdministrationTenantUserItem = ({
  className,
  item,
  isLoading,
  actionButtons,
}: TenantsAdministrationTenantUserItemProps) => {
  const navigate = useNavigate();

  const onStudioClick = () => {
    const href = constructStudioHref(item?.id ?? 0);
    navigate(href);
  };

  return (
    <div
      className={classNames(className, styles.item, {
        [styles.item_deleted]: item?.isDeleted,
      })}
      onClick={onStudioClick}
    >
      <div className={styles.item_left}>
        <SkeletonItem
          isLoading={isLoading}
          value={
            <div className={styles.item_left_name}>
              {item?.name}
              <span className={styles.item_left_name_id}>(ID: {item?.id})</span>
            </div>
          }
          width={150}
        />
      </div>

      {actionButtons}
      <Icon type={IconType.RightChevronIcon} />
    </div>
  );
};
